<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells>
        <app-input
          @input.native="onVendorSearch"
          v-model="search"
          class="input--200"
          type="search"
          placeholder="Search"
          name="search"
        />
        <app-filters
          v-model="vendor_filter"
          :filters="vendor_filters"
          name="filterVendor"
          @change="onChangeFilter"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__number><div class="table-default__left">Number</div></template>
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #h__vendor><div class="table-default__left">Vendor name</div></template>
        <template #h__supplier><div class="table-default__left">Supplier</div></template>
        <template #number="{ row }">
          <button class="table-default__link-border" @click="onShowModal(row)">{{ row.number }}</button>
        </template>
        <template #name="{ row }"><div class="table-default__left">{{ row.name }}</div></template>
        <template #vendor="{ row }"><div class="table-default__left">{{ row.vendor_name }}</div></template>
        <template #supplier="{ row }">
          <div class="table-default__left">{{ row.supplier?.trade_name || '-' }}</div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" :limit="40" />
      </app-cells>
    </template>
    <modal
      name="vendor-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('vendor-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Edit vendor</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Name" label-for="name" required>
              <app-input
                v-model="form.name"
                :error="$v.form.name.$error"
                id="name"
                placeholder="Name"
                name="name"
              />
              <template #error>
                <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Vendor name" label-for="vendor_name" required>
              <app-input
                v-model="form.vendor_name"
                :error="$v.form.vendor_name.$error"
                id="vendor_name"
                placeholder="Vendor name"
                name="vendor_name"
              />
              <template #error>
                <div v-if="$v.form.vendor_name.$dirty && !$v.form.vendor_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Supplier" required>
              <app-select
                @search="onSupplierSearch"
                v-model="form.supplier"
                :options="supplier_options"
                :clearable="false"
                label="trade_name"
                placeholder="Supplier"
                :error="$v.form.supplier.$error"
              />
              <template #error>
                <div v-if="$v.form.supplier.$dirty && !$v.form.supplier.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getAdministrativeSuppliers, getCarManagementVendors, patchCarManagementVendors } from '@/http'
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'

export default {
  name: 'FuelLogVendors',
  data() {
    return {
      page_loaded: false,
      table_preloader: false,
      table: {
        items: [],
        columns: ['number', 'name', 'vendor', 'supplier'],
        options: {
          headings: {
            number: 'Number',
            name: 'Name',
            vendor: 'Vendor name',
            supplier: 'Supplier',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 40,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form: {},
      item_to_action: null,
      supplier_options: [],
      search: null,
      vendor_filter: '',
      vendor_filters: [
        { value: '', label: 'All' },
        { value: 'yes', label: 'Supplier missing' },
        { value: 'no', label: 'Supplier exists' },
      ],
    }
  },
  validations: {
    form: {
      supplier: { required },
      name: { required },
      vendor_name: { required },
    }
  },
  created() {
    this.fetchItems()
  },
  methods: {
    async fetchItems() {
      this.table_preloader = true
      const response = await getCarManagementVendors({
        page: this.pagination.page,
        page_size: 20,
        search: this.search,
        missing_supplier: this.vendor_filter === 'yes' ? true : this.vendor_filter === 'no' ? false : '',
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
      this.page_loaded = true
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      patchCarManagementVendors(this.item_to_action, { ...this.form, supplier: this.form.supplier.id })
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$modal.hide('vendor-modal')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onShowModal(vendor) {
      this.item_to_action = vendor.id
      this.form = { name: vendor.name, vendor_name: vendor.vendor_name, supplier: vendor.supplier || null }
      this.$v.form.$reset()
      this.$modal.show('vendor-modal')
    },
    onChangeFilter() {
      this.search = ''
      this.pagination.page = 1
      this.fetchItems()
    },
    onVendorSearch() {
      this.searchVendor(this)
    },
    searchVendor: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
